import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";

import type {
  BookingDetailResponse,
  CreateBookingPayload,
  CreateBookingResponse,
  BookingFilters,
  BookingsResponse,
  UpdateBookingPnrPayload,
  UpdateBookingPnrResponse,
  UpdatePassengerPayload,
  UpdatePassengerResponse,
} from "../types/bookings.types";

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.bookings;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const bookingsService = {
  async list(filter: BookingFilters): Promise<BookingsResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<BookingsResponse>(endpoints.list(params.toString()), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load bookings due to an unexpected error.",
      ) as unknown as BookingsResponse;
    }
  },

  async details(id: number | string): Promise<BookingDetailResponse> {
    try {
      return await apiClient<BookingDetailResponse>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load booking details due to an unexpected error.",
      ) as unknown as BookingDetailResponse;
    }
  },

  async updatePnr(id: number | string, payload: UpdateBookingPnrPayload): Promise<UpdateBookingPnrResponse> {
    try {      
      return await apiClient<UpdateBookingPnrResponse>(endpoints.updatePnr(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to update the PNR due to an unexpected error.",
      ) as unknown as UpdateBookingPnrResponse;
    }
  },

  async updatePassenger(
    bookingId: number | string,
    passengerId: number | string,
    payload: UpdatePassengerPayload,
  ): Promise<UpdatePassengerResponse> {
    try {
      return await apiClient<UpdatePassengerResponse>(endpoints.updatePassenger(bookingId, passengerId), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to update passenger due to an unexpected error.",
      ) as unknown as UpdatePassengerResponse;
    }
  },

  async create(payload: CreateBookingPayload): Promise<CreateBookingResponse> {
    try {
      return await apiClient<CreateBookingResponse>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to create booking due to an unexpected error.",
      ) as unknown as CreateBookingResponse;
    }
  },
};
